import { Text, Skeleton } from '@mantine/core'
import Link from 'next/link'
import React from 'react'
import { ROUTE } from 'shared-constants'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { COLOR_NAMES, SPACE } from 'Theme'
import { TIER_LADDER, type Tier } from '../tierLadder'

const GREEN = COLOR_NAMES.PINEY_LAKE
const INK = COLOR_NAMES.EEIRE_BLACK
const MUTED = COLOR_NAMES.GRAY_SHINGLE
const LINE = COLOR_NAMES.AMERICAN_SILVER

type TradePerksWidgetProps = {
  currentTier?: Tier
  isLoading?: boolean
}

const PerkRow = ({ label, locked }: { label: string; locked?: boolean }) => (
  <Flex gap={SPACE.PX_8} alignItems="flex-start">
    <Flex
      alignItems="center"
      justifyContent="center"
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        flex: 'none',
        marginTop: 2,
        background: locked ? 'transparent' : COLOR_NAMES.TETON_BREEZE,
        border: locked ? `1.5px dashed ${LINE}` : 'none',
      }}
    >
      {locked ? (
        <Icon.Lock size={10} color={MUTED} />
      ) : (
        <Icon.Checkmark size={12} color={GREEN} />
      )}
    </Flex>
    <Text fz={14} c={locked ? COLOR_NAMES.CARBON_COPY : INK}>
      {label}
    </Text>
  </Flex>
)

const PerksColumnSkeleton = () => (
  <Box>
    <Skeleton height={12} width="50%" mb={16} />
    <Skeleton height={14} width="80%" mb={10} />
    <Skeleton height={14} width="70%" mb={10} />
    <Skeleton height={14} width="75%" />
  </Box>
)

export const TradePerksWidget = ({
  currentTier,
  isLoading,
}: TradePerksWidgetProps) => {
  const rawIdx = TIER_LADDER.findIndex((t) => t.name === currentTier)
  const currentIdx = rawIdx < 0 ? 0 : rawIdx
  const current = TIER_LADDER[currentIdx]
  const next = TIER_LADDER[currentIdx + 1] ?? null

  return (
    <Card
      flexDirection="column"
      width="100%"
      height="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
    >
      <Box p={SPACE.PX_30} width="100%">
        {/* Header */}
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          gap={SPACE.PX_16}
        >
          <Box>
            <Text
              tt="uppercase"
              fz={11}
              fw={600}
              c={MUTED}
              style={{ letterSpacing: '0.14em' }}
            >
              Your perks
            </Text>
            <Text fz={24} fw={500} c={INK} mt={SPACE.PX_4}>
              {current.name} Tier benefits
            </Text>
          </Box>
          {next && (
            <Link href={`${ROUTE.TRADE()}#benefits`}>
              <Text fz={13} fw={600} c={GREEN} style={{ whiteSpace: 'nowrap' }}>
                Compare tiers →
              </Text>
            </Link>
          )}
        </Flex>

        {/* Two columns */}
        <Grid
          gridTemplateColumns={{ MOBILE: '1fr', TABLET: '1fr 1fr' }}
          gap={SPACE.PX_30}
          mt={SPACE.PX_20}
        >
          {isLoading ? (
            <>
              <PerksColumnSkeleton />
              <PerksColumnSkeleton />
            </>
          ) : (
            <>
              <Box>
                <Text
                  tt="uppercase"
                  fz={11}
                  fw={600}
                  c={GREEN}
                  mb={SPACE.PX_12}
                  style={{ letterSpacing: '0.1em' }}
                >
                  Unlocked now
                </Text>
                <Flex flexDirection="column" gap={SPACE.PX_8}>
                  {current.perks.map((perk) => (
                    <PerkRow key={perk} label={perk} />
                  ))}
                </Flex>
              </Box>
              <Box>
                <Text
                  tt="uppercase"
                  fz={11}
                  fw={600}
                  c={MUTED}
                  mb={SPACE.PX_12}
                  style={{ letterSpacing: '0.1em' }}
                >
                  {next ? `Unlocks at ${next.name}` : "You're at the top"}
                </Text>
                {next ? (
                  <Flex flexDirection="column" gap={SPACE.PX_8}>
                    {next.perks.map((perk) => (
                      <PerkRow key={perk} label={perk} locked />
                    ))}
                  </Flex>
                ) : (
                  <Text fz={14} c={MUTED}>
                    Lifetime {current.name} status secured — every benefit,
                    forever.
                  </Text>
                )}
              </Box>
            </>
          )}
        </Grid>
      </Box>
    </Card>
  )
}
